import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { StoryViewer, Big, Label, Sub, plural } from "@/components/wrapped/StoryViewer";
import { Lightning, MoonStars, Heartbeat, Barbell, MapPin, CheckCircle, Trophy } from "@phosphor-icons/react";

const fmtD = (ds) => ds ? new Date(ds + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long" }) : "";
const SPORT_RU = { run: "Бег", bike: "Велосипед", walk: "Ходьба" };

export default function WrappedPage() {
  const [data, setData] = useState();
  useEffect(() => { api.get("/wrapped").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  const slides = useMemo(() => {
    if (!data) return [];
    const s = [];
    s.push({ id: "intro", bg: "radial-gradient(circle at 30% 20%, #1a2b6b 0%, #0b0d12 65%)", render: () => (
      <>
        <Lightning size={56} weight="fill" className="text-[#4dabf5] mb-6" />
        <Label>LifeOS Wrapped</Label>
        <Big color="#fff">{data.year}</Big>
        <Sub>Ваш год в цифрах: сон, восстановление, километры и рекорды. Листайте →</Sub>
      </>
    )});
    if (data.sleep) s.push({ id: "sleep", bg: "radial-gradient(circle at 70% 25%, #3b1a6b 0%, #0b0d12 70%)", render: () => (
      <>
        <MoonStars size={44} weight="fill" className="text-[#a970ff] mb-6" />
        <Label>Сон</Label>
        <Big color="#a970ff">{data.sleep.total_h} ч</Big>
        <Sub>Это <b className="num">{data.sleep.total_days}</b> полных суток сна за {data.sleep.nights} ночей — в среднем <b className="num">{data.sleep.avg_h} ч</b>.
          Лучше всего вы спали в {data.sleep.best_month === "май" ? "мае" : data.sleep.best_month + "е"} ({data.sleep.best_month_avg} ч),
          а рекорд — <b className="num">{data.sleep.longest_h} ч</b> ({fmtD(data.sleep.longest_date)}).</Sub>
      </>
    )});
    if (data.recovery) s.push({ id: "recovery", bg: "radial-gradient(circle at 30% 70%, #0a4d2e 0%, #0b0d12 70%)", render: () => (
      <>
        <Heartbeat size={44} weight="fill" className="text-[#00e676] mb-6" />
        <Label>Восстановление</Label>
        <Big color="#00e676">{data.recovery.avg}%</Big>
        <Sub>Среднее за {data.recovery.days} дней. Зелёных дней — <b className="num">{data.recovery.green_days}</b>, красных — <b className="num">{data.recovery.red_days}</b>.
          Пик — <b className="num">{data.recovery.best}%</b> ({fmtD(data.recovery.best_date)}), лучший месяц — {data.recovery.best_month} ({data.recovery.best_month_avg}%).</Sub>
      </>
    )});
    if (data.strain) s.push({ id: "strain", bg: "radial-gradient(circle at 70% 70%, #6b3a0a 0%, #0b0d12 70%)", render: () => (
      <>
        <Barbell size={44} weight="fill" className="text-[#ffb340] mb-6" />
        <Label>Тренировки</Label>
        <Big color="#ffb340">{data.strain.workouts}</Big>
        <Sub>{data.strain.top_sport && <>Любимый спорт — <b>{data.strain.top_sport.name}</b> ({data.strain.top_sport.count}×). </>}
          {data.strain.kcal > 0 && <>Сожжено <b className="num">{data.strain.kcal.toLocaleString("ru-RU")}</b> ккал. </>}
          {data.strain.hardest && <>Самая тяжёлая — {data.strain.hardest.sport}, нагрузка <b className="num">{data.strain.hardest.strain}</b> ({fmtD(data.strain.hardest.date)}).</>}</Sub>
      </>
    )});
    if (data.gps) s.push({ id: "gps", bg: "radial-gradient(circle at 25% 30%, #0a4d6b 0%, #0b0d12 70%)", render: () => (
      <>
        <MapPin size={44} weight="fill" className="text-[#4dd0e1] mb-6" />
        <Label>GPS-километры</Label>
        <Big color="#4dd0e1">{data.gps.total_km} км</Big>
        <Sub>{data.gps.compare && <>Это {data.gps.compare}! </>}
          {Object.entries(data.gps.by_sport).map(([sp, km]) => `${SPORT_RU[sp] || sp}: ${km} км`).join(" · ")}.
          Самый длинный маршрут — <b className="num">{data.gps.longest_km} км</b> ({fmtD(data.gps.longest_date)}).</Sub>
      </>
    )});
    if (data.life) s.push({ id: "life", bg: "radial-gradient(circle at 70% 30%, #4d0a3a 0%, #0b0d12 70%)", render: () => (
      <>
        <CheckCircle size={44} weight="fill" className="text-[#ff6ec7] mb-6" />
        <Label>Дисциплина</Label>
        <Big color="#ff6ec7">{data.life.habit_completions}</Big>
        <Sub>отметок привычек за год.
          {data.life.best_habit && <> Чемпион — <b>«{data.life.best_habit.name}»</b> ({data.life.best_habit.count}×, серия {data.life.best_habit.streak}).</>}
          {data.life.tasks_done > 0 && <> Закрыто задач: <b className="num">{data.life.tasks_done}</b>.</>}
          {data.life.meals_days > 0 && <> Дневник питания: <b className="num">{data.life.meals_days}</b> дн.</>}</Sub>
      </>
    )});
    s.push({ id: "final", share: true, bg: "radial-gradient(circle at 50% 15%, #4a3a0a 0%, #0b0d12 70%)", render: (finalRef) => (
      <div ref={finalRef} className="flex flex-col items-center text-center px-8 py-10 rounded-3xl"
        style={{ background: "radial-gradient(circle at 50% 0%, #2a2410 0%, #0b0d12 80%)", border: "1px solid rgba(255,215,80,0.25)" }}>
        <Trophy size={44} weight="fill" className="text-amber-300 mb-4" />
        <div className="text-xs uppercase tracking-[0.25em] opacity-70 mb-3">Мой {data.year} · LifeOS</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 my-2">
          {data.sleep && <div><div className="font-heading font-extrabold num text-3xl text-[#a970ff]">{data.sleep.total_h} ч</div><div className="text-[11px] opacity-70">сна</div></div>}
          {data.recovery && <div><div className="font-heading font-extrabold num text-3xl text-[#00e676]">{data.recovery.avg}%</div><div className="text-[11px] opacity-70">восстановление</div></div>}
          {data.strain && <div><div className="font-heading font-extrabold num text-3xl text-[#ffb340]">{data.strain.workouts}</div><div className="text-[11px] opacity-70">тренировок</div></div>}
          {data.gps && <div><div className="font-heading font-extrabold num text-3xl text-[#4dd0e1]">{data.gps.total_km} км</div><div className="text-[11px] opacity-70">по GPS</div></div>}
          {data.life && <div><div className="font-heading font-extrabold num text-3xl text-[#ff6ec7]">{data.life.habit_completions}</div><div className="text-[11px] opacity-70">{plural(data.life.habit_completions, ["отметка привычек", "отметки привычек", "отметок привычек"])}</div></div>}
          {data.recovery && <div><div className="font-heading font-extrabold num text-3xl text-[#00e676]">{data.recovery.green_days}</div><div className="text-[11px] opacity-70">зелёных дней</div></div>}
        </div>
        <div className="text-[10px] opacity-50 mt-3">rukos.life</div>
      </div>
    )});
    return s;
  }, [data]);

  if (data === undefined) return <div className="glass-card h-40 animate-pulse mt-10" />;
  if (!data || !data.has_data) return (
    <div className="glass-card p-8 text-center mt-10" data-testid="wrapped-no-data">
      <Trophy size={40} className="text-muted-foreground mx-auto mb-3" />
      <p className="font-semibold mb-1">Пока нечего показывать</p>
      <p className="text-sm text-muted-foreground">Подключите Whoop, запишите GPS-тренировки или отмечайте привычки — и годовой отчёт соберётся сам.</p>
    </div>
  );

  return <StoryViewer slides={slides} shareName={`lifeos-wrapped-${data.year}`} />;
}
